import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X, ArrowLeft } from 'lucide-react'
import SelectAndSearch from '../../SelectAndSearch'
import InfiniteSelect from '../../InfiniteSelect'
import axios from '../../../config/axios'

interface PropsInterface {
  stepId: number
  closeModal: () => void
  editingField?: { id: number, name: string, typeFieldId?: number, typeField?: { id: number, name: string } }
  onSaved?: (field?: { id: number, name: string }) => void
  onBack?: () => void
}

const AddFieldOnStep = ({ stepId, closeModal, editingField, onSaved, onBack }: PropsInterface) => {
  const [ fieldTypes, setFieldTypes ] = useState<{id: number, name: string}[]>([])
  const [ formData, setFormData ] = useState<{field: {id: number, name: string}, typeField: {id: number, name: string}}>({field: {id: 0, name: ''}, typeField: {id: 0, name: ''}})
  const [ fields, setFields ] = useState<{id: number, name: string, typeField: {id: number, name: string}}[]>([])
  const [ createdFieldId, setCreatedFieldId ] = useState<number | null>(null)
  const [ isRequired, setIsRequired ] = useState<boolean>(false)
  const lastCreatedIdRef = useRef<number | null>(null)
  const isEditing = !!editingField
  const isTypeDisabled = !(formData.field.id && createdFieldId !== null && Number(formData.field.id) === Number(createdFieldId))

  useEffect(() => {
    const fetchFieldTypes = async () => {
      try {
        console.log('📋 [FETCH FIELD TYPES] Buscando tipos de campo...');
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/types`)
        if(response.data) {
          console.log('📋 [FETCH FIELD TYPES] Tipos retornados:', response.data.data.length);
          console.log('📋 [FETCH FIELD TYPES] Detalhes:', response.data.data.map((t: any) => ({ id: t.id, name: t.name })));
          setFieldTypes(response.data.data)
        }
      } catch (error) {
        console.error('❌ [FETCH FIELD TYPES] Erro:', error)
      }
    }

    fetchFieldTypes()
  }, [])

  useEffect(() => {
    const fetchFields = async () => {
      try {
        console.log('🔍 [FETCH INITIAL FIELDS] Buscando campos iniciais...');
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`)
        if(response.data) {
          const allFields = response.data.data || [];
          console.log('🔍 [FETCH INITIAL FIELDS] Total de campos:', allFields.length);
          console.log('🔍 [FETCH INITIAL FIELDS] Campos integrados:', allFields.filter((f: any) => f.integrationConfig).length);
          
          const filtered = allFields.filter((f: any) => !f.integrationConfig);
          console.log('🟢 [FETCH INITIAL FIELDS] Campos personalizados (filtrados):', filtered.length);
          
          setFields(filtered)
        }
      } catch (error) {
        console.error('❌ [FETCH INITIAL FIELDS] Erro:', error)
      }
    }

    fetchFields()
  }, [])

  useEffect(() => {
    if (!isEditing && formData.field.id === 0 && formData.typeField.id === 0 && fieldTypes.length > 0) {
      const first = fieldTypes[0]
      setFormData((prev) => ({ ...prev, typeField: { id: first.id, name: first.name } }))
    }
  }, [fieldTypes, isEditing, formData.typeField.id, formData.field.id])

  useEffect(() => {
    if (editingField) {
      const typeFromList = fieldTypes.find((t) => t.id === (editingField.typeField?.id || editingField.typeFieldId))
      const fallbackTypeId = editingField.typeField?.id || editingField.typeFieldId || 0
      const fallbackTypeName = typeFromList?.name || editingField.typeField?.name || ''
      setFormData({
        field: { id: editingField.id, name: editingField.name },
        typeField: typeFromList || { id: fallbackTypeId, name: fallbackTypeName }
      })
      setCreatedFieldId(null)
      lastCreatedIdRef.current = null
      return
    }
    setFormData({ field: { id: 0, name: '' }, typeField: { id: 0, name: '' } })
    setCreatedFieldId(null)
    lastCreatedIdRef.current = null
  }, [editingField, fieldTypes])

  const handleSelectField = (field: Partial<{id: number, name: string}>) => {
    const selectedType = fields.find((f) => f.id === field.id)?.typeField
    setFormData(prev => ({
      ...prev,
      field: {id: field.id || 0, name: field.name || ''},
      typeField: selectedType || prev.typeField
    }))
    if (field.id && lastCreatedIdRef.current !== null && Number(field.id) === Number(lastCreatedIdRef.current)) {
      setCreatedFieldId(lastCreatedIdRef.current)
    } else {
      setCreatedFieldId(null)
      lastCreatedIdRef.current = null
    }
  }

  const handleSelectTypeField = (typeField: Partial<{id: number, name: string}>) => {
    setFormData(prev => ({
      ...prev,
      typeField: {id: typeField.id || 0, name: typeField.name || ''}
    }))
  }

  const handleSaveFieldOnStep = async () => {
    try {
      const isSameFieldAsEditing = isEditing && editingField && formData.field.id === editingField.id

      if (isSameFieldAsEditing && editingField) {
        const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${editingField.id}`, {
          name: editingField.name,
          typeFieldId: formData.typeField.id || undefined
        })
        if (response.data) {
          onSaved?.(response.data.data)
          closeModal()
        }
        return
      }


      if (formData.field.id && formData.typeField.id) {
        const selectedField = fields.find((f) => f.id === formData.field.id)
        const currentTypeId = selectedField?.typeField?.id || (selectedField as any)?.typeFieldId
        if (!currentTypeId || currentTypeId !== formData.typeField.id) {
          const patchResponse = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${formData.field.id}`, {
            typeFieldId: formData.typeField.id
          })
          const updated = patchResponse.data?.data || patchResponse.data || {}
          const updatedType = updated.typeField || { id: formData.typeField.id, name: formData.typeField.name }
          setFields((prev) => prev.map((f) => f.id === formData.field.id ? { ...f, typeField: updatedType, typeFieldId: updatedType.id } : f))
        }
      }

      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field`, {
        pipelineFieldId: formData.field.id,
        isRequired: isRequired
      })
      if(response.data) {
        onSaved?.()
        closeModal()
      }
    } catch (error) {
      console.error(error)
    }
  }
  
  return (
    <ModalTest>
      <S.ContainerOfModal width='700px'>
        <S.Header>
          <S.HeaderTitle>{isEditing ? 'Editar campo' : 'Adicionar campo personalizado'}</S.HeaderTitle>
          <S.CloseDiv onClick={() => closeModal()}>
            <X size={20} />
          </S.CloseDiv>
        </S.Header>
        <S.FormContainer>
          <InfiniteSelect
            label='Selecione o campo'
            placeholder='Digite aqui'
            value={formData.field.id ? { id: formData.field.id, name: formData.field.name } : null}
            onSelect={(option) => {
              if (!option) {
                setFormData((prev) => ({ ...prev, field: { id: 0, name: '' }, typeField: { id: 0, name: '' } }))
                setCreatedFieldId(null)
                return
              }
              handleSelectField({ id: option.id, name: option.name })
              if (option.typeField) {
                setFormData((prev) => ({ ...prev, typeField: option.typeField }))
              }
            }}
            allowCreate
            allowDelete
            mode='crud'
            endpointConfig={{
              listPath: '/pipelines/pipeline-field',
              createPath: '/pipelines/pipeline-field',
              deletePath: '/pipelines/pipeline-field'
            }}
            fetchFunction={async (_limit, _offset, search) => {
              console.log('🔍 [FETCH FIELDS] Buscando campos...', { search });
              const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`, {
                params: { search }
              })
              const raw = response.data?.data || []
              console.log('🔍 [FETCH FIELDS] Campos retornados do backend:', raw.length);
              console.log('🔍 [FETCH FIELDS] Campos integrados:', raw.filter((f: any) => f.integrationConfig).length);
              console.log('🔍 [FETCH FIELDS] Detalhes:', raw.map((f: any) => ({ id: f.id, name: f.name, isIntegrated: !!f.integrationConfig })));
              
              const filtered = raw.filter((f: any) => !f.integrationConfig);
              console.log('🟢 [FETCH FIELDS] Campos após filtrar integrados:', filtered.length);
              
              const normalized = filtered.map((item: any) => {
                const fallbackType = fieldTypes.find((t) => t.id === item.typeFieldId)
                return {
                  ...item,
                  typeField: item.typeField || (item.typeFieldId ? { id: item.typeFieldId, name: fallbackType?.name || '' } : fallbackType)
                }
              })
              console.log('🔍 [FETCH FIELDS] Campos normalizados:', normalized.length);
              setFields(normalized)
              return normalized
            }}
            onItemCreate={async (name) => {
              let typeId = formData.typeField.id
              if (!typeId && fieldTypes.length > 0) {
                typeId = fieldTypes[0].id
                setFormData((prev) => ({ ...prev, typeField: { id: typeId, name: fieldTypes[0].name } }))
              }
              if (!typeId) throw new Error('Selecione um tipo')
              const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`, {
                name,
                typeFieldId: typeId
              })
              const created = response.data?.data || response.data
              if (created) {
                setFields((prev) => [{ id: created.id, name: created.name, typeField: created.typeField }, ...prev])
                setFormData((prev) => ({ ...prev, field: { id: created.id, name: created.name } }))
                lastCreatedIdRef.current = Number(created.id)
                setCreatedFieldId(Number(created.id))
              }
              return created
            }}
            onItemDelete={async (id) => {
              await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${id}`)
              setFields((prev) => prev.filter((f) => f.id !== id))
              setFormData((prev) => prev.field.id === id
                ? { ...prev, field: { id: 0, name: '' }, typeField: { id: 0, name: '' } }
                : prev)
            }}
            getOptionLabel={(option) => option.name}
            options={fields}
            initialOptions={fields}
          />
          <SelectAndSearch
            label='Selecione o tipo'
            options={fieldTypes}
            onSelect={handleSelectTypeField}
            selectedOption={formData.typeField}
            disabled={isTypeDisabled}
          />
          <SwitchContainer>
            <SwitchTitle>Solicitar preenchimento</SwitchTitle>
            <Switch active={isRequired} onClick={() => setIsRequired(!isRequired)}>
              <SwitchThumb active={isRequired}>
                {isRequired && (
                  <CheckIcon viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </CheckIcon>
                )}
              </SwitchThumb>
            </Switch>
          </SwitchContainer>
        </S.FormContainer>
        <S.BottomDiv>
          <ButtonsRow>
            <CancelText onClick={() => closeModal()}>Cancelar</CancelText>
            {onBack && (
              <BackButton onClick={onBack}>
                <ArrowLeft size={16} /> Voltar
              </BackButton>
            )}
            <AddButton onClick={handleSaveFieldOnStep} disabled={!isEditing && formData.field.id === 0}>
              + {isEditing ? 'Alterar campo' : 'Adicionar campo'}
            </AddButton>
          </ButtonsRow>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

const ButtonsRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`

const CancelText = styled.span`
  color: #0090D3;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0.7rem 1rem;

  &:hover {
    text-decoration: underline;
  }
`

const BackButton = styled.button`
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  border-radius: 1.5rem;
  border: 1px solid #0090D3;
  color: #0090D3;
  background-color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f9ff;
  }
`

const AddButton = styled.button`
  background-color: #0090D3;
  color: #fff;
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 1.5rem;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #007ab8;
  }
`

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`

const SwitchTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const Switch = styled.div<{ active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
`

const SwitchThumb = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckIcon = styled.svg`
  width: 12px;
  height: 12px;
  fill: none;
  stroke: #0090D3;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
`

export default AddFieldOnStep